import React, { Fragment, useState } from 'react';
import {
  Box,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { VocabularyIrregularVerb } from 'app/types';
import AudioButton from 'components/dictionaryView/AudioButton';
import { DictionaryWord } from 'app/types';
import StatusCell from 'components/vocabularyWordsView/StatusCell';
import GroupHeader from 'components/vocabularyWordsView/GroupHeader';
import { SELECT_COL_WIDTH } from 'components/vocabularyWordsView/primitives';
import {
  GroupedWords,
  UseVocabularyWordsViewState,
} from 'components/vocabularyWordsView/useVocabularyWordsView';
import { Checkbox, LevelPill } from './primitives';
import {
  WordStatus,
  hexToRgba,
  statusColor,
  statusOfVerb,
} from './meta';
import { UseVocabularyVerbsViewState } from './useVocabularyVerbsView';

interface ManagerVerbsTableProps {
  view: UseVocabularyVerbsViewState;
  /** Adapter — VocabularyIrregularVerb → DictionaryWord-shaped object for the
   * shared StatusCell + GroupHeader. They only read `id` / `status` from the
   * verb so any object satisfying that shape works. */
  onSetStatus: (id: number, status: WordStatus) => void;
  onDeleteOne: (id: number) => void;
}

interface ColMeta {
  key: string;
  label: string;
  width: string;
  min: number;
}

const COLS: ColMeta[] = [
  { key: 'number', label: '№', width: '46px', min: 46 },
  { key: 'level', label: 'Рівень', width: '70px', min: 70 },
  { key: 'infinitive', label: 'Infinitive', width: '170px', min: 170 },
  { key: 'pastSimple', label: 'Past Simple', width: '170px', min: 170 },
  { key: 'pastParticiple', label: 'Past Participle', width: '180px', min: 180 },
  { key: 'translation', label: 'Переклад', width: 'minmax(160px, 1fr)', min: 160 },
  { key: 'status', label: 'Статус', width: '184px', min: 184 },
  { key: 'actions', label: '', width: '92px', min: 92 },
];

const minWidth = SELECT_COL_WIDTH + COLS.reduce((s, c) => s + c.min, 0);

const template = `${SELECT_COL_WIDTH}px ${COLS.map((c) => c.width).join(' ')}`;

const rowTint = (
  v: VocabularyIrregularVerb,
  selected: boolean,
  highlight: boolean,
  hlLearned: boolean,
  hlActive: boolean,
  mode: 'light' | 'dark',
): string | undefined => {
  if (selected) {
    return mode === 'light' ? 'rgba(59,44,147,0.07)' : 'rgba(155,124,255,0.14)';
  }
  if (highlight) {
    return mode === 'light' ? 'rgba(39,204,188,0.14)' : 'rgba(63,224,206,0.18)';
  }
  const s = statusOfVerb(v);
  if (s === 'ACTIVE' && hlActive) return hexToRgba(statusColor('ACTIVE', mode), 0.08);
  if (s === 'LEARNED' && hlLearned) return hexToRgba(statusColor('LEARNED', mode), 0.07);
  return undefined;
};

const HeaderRow: React.FC<{ view: UseVocabularyVerbsViewState }> = ({ view }) => (
  <Box
    sx={{
      alignItems: 'center',
      bgcolor: 'primary.tableHeadBg',
      borderBottom: '1px solid',
      borderColor: 'primary.tableBorder',
      display: 'grid',
      gap: 0,
      gridTemplateColumns: template,
      height: 46,
      px: 1,
    }}
  >
    <Stack direction={'row'} justifyContent={'center'}>
      <Checkbox checked={view.allSelected} onClick={() => view.toggleAll()} />
    </Stack>
    {COLS.map((c) => (
      <Typography
        key={c.key}
        sx={{
          color: 'text.secondary',
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: '0.04em',
          px: 1.5,
          textTransform: 'uppercase',
        }}
      >
        {c.label}
      </Typography>
    ))}
  </Box>
);

const EmptyState: React.FC = () => (
  <Box sx={{ color: 'text.secondary', px: 3, py: 6, textAlign: 'center' }}>
    <SearchOffIcon sx={{ color: 'primary.tableBorder', fontSize: 34 }} />
    <Typography sx={{ fontSize: 15, fontWeight: 600, mt: 1 }}>
      Нічого не знайдено
    </Typography>
    <Typography sx={{ fontSize: 13.5, mt: 0.25 }}>
      Спробуй змінити фільтри або пошук
    </Typography>
  </Box>
);

const FormCell: React.FC<{ word: string; transcription?: string; strong?: boolean; primary?: boolean }> = ({
  word,
  transcription,
  strong,
  primary,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <AudioButton word={word} size={28} />
      <Box
        sx={{
          fontSize: 15.5,
          fontWeight: strong ? 800 : 700,
          color: primary ? 'primary.main' : 'text.primary',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {word}
      </Box>
    </Box>
    {transcription ? (
      <Box
        sx={{
          fontSize: 12.5,
          color: 'text.secondary',
          fontFamily: 'ui-monospace, monospace',
          mt: 0.375,
          pl: 4.5,
        }}
      >
        {transcription}
      </Box>
    ) : null}
  </Box>
);

const RowEditField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}> = ({ value, onChange, placeholder }) => (
  <TextField
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    size={'small'}
    onClick={(e) => e.stopPropagation()}
    autoComplete={'off'}
    sx={{
      width: '100%',
      '& .MuiInputBase-root': {
        bgcolor: 'background.default',
        fontSize: 14,
        height: 34,
      },
      '& fieldset': { borderColor: 'primary.main' },
    }}
  />
);

interface TableRowProps {
  verb: VocabularyIrregularVerb;
  index: number;
  view: UseVocabularyVerbsViewState;
  onSetStatus: (id: number, status: WordStatus) => void;
  onDeleteOne: (id: number) => void;
}

const TableRow: React.FC<TableRowProps> = ({
  verb,
  index,
  view,
  onSetStatus,
}) => {
  const theme = useTheme();
  const mode: 'light' | 'dark' = theme.palette.mode === 'dark' ? 'dark' : 'light';
  const [hover, setHover] = useState(false);
  const selected = !!view.selected[verb.id];
  const editing = view.isEditing(verb.id);
  const draft = editing ? view.draftFor(verb.id) : null;
  const tint = rowTint(
    verb,
    selected,
    verb.id === view.highlightId,
    view.settings.hlLearned,
    view.settings.hlActive,
    mode,
  );

  return (
    <Box
      onClick={() => !editing && view.toggle(verb.id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        alignItems: 'center',
        bgcolor: tint ?? 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'primary.tableBorder',
        boxShadow:
          hover && !selected && !editing
            ? `inset 0 0 0 1.5px ${theme.palette.primary.main}`
            : 'none',
        cursor: editing ? 'default' : 'pointer',
        display: 'grid',
        gap: 0,
        gridTemplateColumns: template,
        minHeight: 60,
        px: 1,
        transition: 'background-color .12s ease',
      }}
    >
      <Stack direction={'row'} justifyContent={'center'}>
        <Checkbox
          checked={selected}
          onClick={(e) => {
            e.stopPropagation();
            view.toggle(verb.id);
          }}
        />
      </Stack>

      {/* number */}
      <Box sx={{ px: 1.5, py: 1, fontSize: 13.5, color: 'text.secondary' }}>
        {index + 1}
      </Box>

      {/* level */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <LevelPill level={verb.levelBased} />
      </Box>

      {/* infinitive */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <FormCell
          word={verb.infinitive}
          transcription={verb.infinitiveTranscription}
          strong
          primary
        />
      </Box>

      {/* past simple */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <FormCell word={verb.pastSimple} transcription={verb.pastSimpleTranscription} />
      </Box>

      {/* past participle */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <FormCell word={verb.pastParticiple} transcription={verb.pastParticipleTranscription} />
      </Box>

      {/* translation (inline-editable) */}
      <Box sx={{ px: 1.5, py: 1, minWidth: 0 }}>
        {editing && draft ? (
          <RowEditField
            value={draft.translation}
            onChange={(v) => view.setDraft(verb.id, { translation: v })}
            placeholder={'переклад'}
          />
        ) : (
          <Box
            sx={{
              fontSize: 14.5,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {verb.translation}
          </Box>
        )}
      </Box>

      {/* status (shared cell — reads `status` from the row) */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <StatusCell
          word={{
            id: verb.id,
            status: statusOfVerb(verb),
          } as unknown as DictionaryWord}
          onSetStatus={onSetStatus}
        />
      </Box>

      {/* actions */}
      <Box sx={{ px: 1, py: 1, display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
        {editing ? (
          <>
            <Tooltip title={'Зберегти'}>
              <IconButton
                size={'small'}
                onClick={(e) => {
                  e.stopPropagation();
                  view.saveEdit(verb.id);
                }}
                sx={{ bgcolor: 'primary.main', color: 'common.white', '&:hover': { bgcolor: 'primary.main' } }}
              >
                <CheckIcon fontSize={'small'} />
              </IconButton>
            </Tooltip>
            <Tooltip title={'Скасувати'}>
              <IconButton
                size={'small'}
                onClick={(e) => {
                  e.stopPropagation();
                  view.cancelEdit(verb.id);
                }}
              >
                <CloseIcon fontSize={'small'} />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          hover && (
            // Row-level delete intentionally removed — destructive action
            // lives only on the bulk Selection bar (Видалити) so a single
            // mis-click cannot wipe a verb without a deliberate selection.
            <Tooltip title={'Редагувати'}>
              <IconButton
                size={'small'}
                onClick={(e) => {
                  e.stopPropagation();
                  view.startEdit([verb.id]);
                }}
                sx={{ color: 'text.secondary' }}
              >
                <EditOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )
        )}
      </Box>
    </Box>
  );
};

const ManagerVerbsTable: React.FC<ManagerVerbsTableProps> = ({
  view,
  onSetStatus,
  onDeleteOne,
}) => {
  let running = 0;
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth }}>
          <HeaderRow view={view} />
          {view.filtered.length === 0 && <EmptyState />}
          {view.grouped.map((g) => (
            <Fragment key={g.key}>
              {g.label !== null && (
                <GroupHeader
                  group={{
                    key: g.key,
                    label: g.label,
                    words: g.verbs,
                    ids: g.ids,
                  } as unknown as GroupedWords}
                  view={view as unknown as UseVocabularyWordsViewState}
                />
              )}
              {g.verbs.map((v) => {
                const idx = running++;
                return (
                  <TableRow
                    key={v.id}
                    verb={v}
                    index={idx}
                    view={view}
                    onSetStatus={onSetStatus}
                    onDeleteOne={onDeleteOne}
                  />
                );
              })}
            </Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ManagerVerbsTable;
